/**
 * Funktionsrollen eines Nutzers anzeigen, entziehen und vergeben — EINE Bedienung für den
 * Admin-Reiter „Benutzer & Rollen" (`UserRow`) und den Reiter „Neue Nutzer" (#116). Was der
 * eingeloggte Actor sehen und tun darf, entscheiden `admin-gating.ts` und die shared Prädikate
 * (`canAssignFunctionRole`) — hier nichts nachgebaut.
 */
import {
  FUNCTION_ROLE_LABELS,
  type FunctionRole,
  FunctionRoleSchema,
  type PermissionUser,
  type Segment,
  SegmentSchema,
  canAssignFunctionRole,
  isSegmentScopedRole,
} from '@projektverwaltung/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  assignableFunctionRoles,
  assignableSegments,
  canOfferRoleAssignment,
} from '../../lib/admin-gating.js';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';

const SEGMENTS: Segment[] = SegmentSchema.options;

/**
 * F1 Task 2 Fix-Welle 1: „keine" darf nicht neben dem Auto-PL-Badge stehen —
 * ein reiner Auto-PL-User (der Kernfall: keine manuelle Funktionsrolle, PL
 * nur über `project_lead_user_id` abgeleitet) hätte sonst „keine" UND
 * „automatisch: PL" nebeneinander gesehen. Als reine Funktion extrahiert,
 * damit die Bedingung per Logik-Test (nicht Komponententest) abgesichert ist.
 */
export function shouldShowNoRolesHint(user: {
  function_roles: readonly unknown[];
  is_auto_pl?: boolean | undefined;
}): boolean {
  return user.function_roles.length === 0 && !user.is_auto_pl;
}

export type FunctionRoleEditorUser = {
  function_roles: ReadonlyArray<{
    user_function_role_id: string;
    role: string;
    scope_segment: string | null;
  }>;
  is_auto_pl?: boolean | undefined;
};

/**
 * Rollen vergeben/entziehen gegen die bestehenden Endpunkte. `onDone` läuft nach Erfolg
 * (Listen neu laden); Fehler werden als Toast gezeigt.
 */
export function useFunctionRoleMutations(onDone: () => void) {
  const { t } = useTranslation();
  const toast = useToast();
  const assign = useMutation({
    mutationFn: ({
      userId,
      ...data
    }: { userId: string } & Parameters<typeof api.adminAssignFunctionRole>[1]) =>
      api.adminAssignFunctionRole(userId, data),
    onSuccess: onDone,
    onError: (err) =>
      toast.error(t('admin.role_assign_failed', 'Rolle nicht zugewiesen'), (err as Error).message),
  });
  const unassign = useMutation({
    mutationFn: api.adminRemoveFunctionRole,
    onSuccess: onDone,
    onError: (err) =>
      toast.error(t('admin.role_unassign_failed', 'Rolle nicht entzogen'), (err as Error).message),
  });
  return { assign, unassign };
}

/** Standard-Aufräumen nach Rollenänderung — dieselben Listen wie in `UserAdmin`. */
export function useInvalidateNachRollenaenderung(): () => void {
  const queryClient = useQueryClient();
  return () => {
    for (const key of ['admin-users', 'dev-users', 'users', 'users-all', 'neue-nutzer']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
}

export function FunctionRoleEditor({
  user,
  actor,
  isSelf,
  inaktiv = false,
  extra,
  onAssign,
  onUnassign,
}: {
  user: FunctionRoleEditorUser;
  /** Der eingeloggte User, der bedient (Gating-Basis, nicht `user` selbst!). */
  actor: PermissionUser;
  isSelf: boolean;
  /** Inaktive bekommen keine Vergabe (das Backend lehnt sie mit 409 `user_inaktiv` ab). */
  inaktiv?: boolean;
  /** Zusatzinhalt zwischen Rollen-Chips und Vergabe-Formular (z. B. Manager-Tags). */
  extra?: ReactNode;
  onAssign: (role: FunctionRole, scope: Segment | null) => void;
  onUnassign: (functionRoleId: string) => void;
}) {
  const { t } = useTranslation();
  // F2: Rollen-Dropdown + Segment-Auswahl sind actor-abhängig (assignableFunctionRoles/
  // assignableSegments aus admin-gating.ts) — für KuSe-Admin bereits vorgefiltert auf das,
  // was `canAssignFunctionRole` ihm ohnehin erlauben würde.
  const roleOptions = assignableFunctionRoles(actor);
  const restrictedSegments = assignableSegments(actor);
  const fixedSegment =
    restrictedSegments && restrictedSegments.length === 1 ? restrictedSegments[0] : null;
  const [newRole, setNewRole] = useState<FunctionRole>(roleOptions[0] ?? 'projektleiter');
  const [newScope, setNewScope] = useState<Segment | ''>(fixedSegment ?? '');

  const scopeNeedsSegment = isSegmentScopedRole(newRole);
  const segmentOptions = restrictedSegments ?? SEGMENTS;

  return (
    <>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {shouldShowNoRolesHint(user) && (
          <span className="text-xs text-[color:var(--ink-4)] italic">
            {t('admin.roles_none', 'keine')}
          </span>
        )}
        {user.function_roles.map((fr) => {
          const rolle = FunctionRoleSchema.safeParse(fr.role);
          const scope = SegmentSchema.nullable().safeParse(fr.scope_segment);
          return (
            <span
              key={fr.user_function_role_id}
              className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-[var(--surface-2)] text-[color:var(--ink)]"
            >
              {rolle.success ? FUNCTION_ROLE_LABELS[rolle.data] : fr.role}
              {fr.scope_segment && (
                <span className="text-[color:var(--ink-3)]">({fr.scope_segment})</span>
              )}
              {/* F2: Löschen (= Rollenentzug) nur wo dieselbe Prädikat-Funktion es auch
                  vergeben ließe — shared Single Source of Truth, hier importiert statt
                  nachgebaut (Task-Brief §3). */}
              {rolle.success &&
                scope.success &&
                canAssignFunctionRole(actor, rolle.data, scope.data) && (
                  <button
                    type="button"
                    onClick={() => onUnassign(fr.user_function_role_id)}
                    className="text-[color:var(--ink-4)] hover:text-[color:var(--danger)] ml-0.5"
                    title={t('admin.role_remove', 'Rolle entfernen')}
                  >
                    ✕
                  </button>
                )}
            </span>
          );
        })}
        {user.is_auto_pl && (
          // Reine Anzeige — nicht löschbar/interaktiv (keine DB-Zeile, s. Task 1).
          <span className="inline-flex items-center text-[11px] px-1.5 py-0.5 rounded bg-[var(--surface-2)] text-[color:var(--ink-3)] italic">
            {t('admin.role_auto_pl', 'automatisch: PL')}
          </span>
        )}
      </div>
      {extra}
      {/* I1 (Final-Review): Selbst-Eskalations-Riegel im Frontend — ein KuSe-Admin (ohne
          is_admin) bekommt für die EIGENE Zeile den Vergabe-Bereich nicht angeboten, weil
          das Backend eine Selbstvergabe seit I1 ohnehin mit 403 ablehnt. is_admin bleibt
          unberührt (siehe canOfferRoleAssignment). */}
      {inaktiv ? null : canOfferRoleAssignment(actor, isSelf) ? (
        <div className="flex gap-1.5">
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as FunctionRole)}
            aria-label={t('admin.role_select_label', 'Rolle')}
            className="text-xs border border-[color:var(--line)] rounded px-1.5 py-1 bg-[var(--surface)]"
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {FUNCTION_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          <select
            value={newScope}
            onChange={(e) => setNewScope(e.target.value as Segment | '')}
            disabled={!scopeNeedsSegment || fixedSegment !== null}
            aria-label={t('admin.scope_select_label', 'Segment')}
            title={
              fixedSegment
                ? t('admin.scope_fixed_hint', 'Segment fest auf dein Kundensegment vorbelegt')
                : undefined
            }
            className="text-xs border border-[color:var(--line)] rounded px-1.5 py-1 bg-[var(--surface)] disabled:opacity-40"
          >
            <option value="">
              {scopeNeedsSegment
                ? t('admin.scope_choose_segment', '— Segment wählen —')
                : t('admin.scope_global', 'global')}
            </option>
            {segmentOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              if (scopeNeedsSegment && !newScope) return;
              onAssign(newRole, scopeNeedsSegment ? (newScope as Segment) : null);
              setNewScope(fixedSegment ?? '');
            }}
            disabled={scopeNeedsSegment && !newScope}
            aria-label={t('admin.role_assign_btn', 'Rolle zuweisen')}
            className="text-xs px-2 py-1 rounded bg-[var(--primary)] text-[color:var(--on-primary)] disabled:opacity-40"
          >
            +
          </button>
        </div>
      ) : (
        <p className="text-[11px] text-[color:var(--ink-4)] italic">
          {t(
            'admin.role_assign_self_blocked',
            'Rollenvergabe an die eigene Person ist nicht möglich.',
          )}
        </p>
      )}
    </>
  );
}
